/**
 * UomPicker.jsx — Reusable dropdown for picking a UOM at quantity-entry time.
 *
 * Used by Receiving (manual + scan + batch), Manual Order, Cycle Counts, etc.
 *
 * Props:
 *   value           Current UOM code (lowercased, e.g. "L" or "case" or base)
 *   onChange        (code, factor) => void
 *   sku             SKU doc with `unit_of_measure` (base) + `uom_conversions`
 *   className       Extra Tailwind classes for the <select>
 *   disabled        Optional
 *   testId          Optional data-testid override
 *
 * Renders nothing when the SKU has no conversions and base UOM is "each"
 * (most warehouses don't care about UOM for discrete unit SKUs).
 */
import React, { useMemo } from 'react';

export const buildUomOptions = (sku) => {
  if (!sku) return [];
  const base = (sku.unit_of_measure || 'each').toLowerCase();
  const convs = Array.isArray(sku.uom_conversions) ? sku.uom_conversions : [];
  const basePrice = sku.unit_price != null ? Number(sku.unit_price) : null;
  const opts = [{ code: base, factor: 1, label: null, is_base: true, unit_price: basePrice }];
  convs.forEach((r) => opts.push({
    code: (r.code || '').toLowerCase(),
    factor: Number(r.factor) || 1,
    label: r.label || null,
    is_base: false,
    unit_price: r.unit_price != null ? Number(r.unit_price) : null,
  }));
  return opts;
};

export default function UomPicker({ value, onChange, sku, className = '', disabled = false, testId }) {
  const opts = useMemo(() => buildUomOptions(sku), [sku]);
  // Hide entirely when the SKU has zero conversions AND base is "each".
  // Otherwise even a SKU with base 'kg' will show a single 'kg' option so
  // operators get visual confirmation of the unit.
  if (opts.length <= 1 && (opts[0]?.code || 'each') === 'each') return null;

  const current = value || opts[0]?.code || 'each';
  return (
    <select
      value={current}
      disabled={disabled}
      onChange={(e) => {
        const code = e.target.value;
        const found = opts.find((o) => o.code === code);
        onChange?.(code, found ? Number(found.factor) || 1 : 1);
      }}
      data-testid={testId || 'uom-picker'}
      className={`h-9 px-2 text-xs bg-background border border-border font-mono uppercase ${className}`}
      title={(() => {
        const o = opts.find((x) => x.code === current);
        if (!o) return '';
        if (o.is_base) return `Base unit (1 ${o.code} = 1 ${o.code})`;
        return o.label ? `${o.label} · 1 ${o.code} = ${o.factor} ${opts[0].code}` : `1 ${o.code} = ${o.factor} ${opts[0].code}`;
      })()}
    >
      {opts.map((o) => {
        const priceTxt = (o.unit_price != null) ? ` · $${o.unit_price.toFixed(2)}` : '';
        const factorTxt = (o.factor && o.factor !== 1) ? ` · ×${o.factor}` : '';
        return (
          <option key={o.code} value={o.code}>
            {o.is_base ? `${o.code} (base)${priceTxt}` : `${o.code}${factorTxt}${priceTxt}`}
          </option>
        );
      })}
    </select>
  );
}
